import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  User, Phone, Mail, Calendar, MapPin, DollarSign, Home,
  ArrowRight, X, Search, Plus, Clock, Building, Bookmark, FileText,
  MessageSquare, Send, Ban, CheckCircle2, AlertTriangle, Loader2,
  PhoneCall, PhoneOff, PhoneMissed, Voicemail, ArrowUpRight,
  Building2, TrendingDown, Bell, ExternalLink, Upload, Users, Briefcase,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useScheduleRecommendations, InlineTimeSuggestion } from '../components/ScheduleSuggestions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_STYLES = {
  buyer: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  seller: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  investor: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  unknown: 'bg-secondary text-muted-foreground border-border',
};

const TEMP_STYLES = {
  hot: 'bg-red-500/10 text-red-400 border-red-500/20',
  warm: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  cold: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
};

const PRIORITY_STYLES = {
  hot: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', label: 'Hot' },
  warm: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', label: 'Warm' },
  cold: { bg: 'bg-secondary', text: 'text-muted-foreground', border: 'border-border', label: 'Cold' },
};

const STATUS_STYLES = {
  new: 'bg-emerald-500/10 text-emerald-400',
  contacted: 'bg-blue-500/10 text-blue-400',
  qualified: 'bg-violet-500/10 text-violet-400',
  lost: 'bg-red-500/10 text-red-400',
  active: 'bg-teal-500/10 text-teal-400',
};

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/* ===== ADD LEAD MODAL ===== */
function AddLeadModal({ open, onClose, token, onCreated }) {
  const [form, setForm] = useState({
    name: '', company: '', needs: '', job_title: '', email: '', phone: '',
    alt_phones: '', status: 'new', notes: '', assigned_to: '', last_contact: '',
    referral_source: '', lead_type: 'unknown',
  });
  const [saving, setSaving] = useState(false);
  if (!open) return null;

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        alt_phones: form.alt_phones ? form.alt_phones.split(',').map(p => p.trim()).filter(Boolean) : [],
      };
      await axios.post(`${API}/leads/`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Lead created');
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  const F = (label, key, placeholder, type = 'text') => (
    <div>
      <Label className="text-foreground text-xs">{label}</Label>
      <Input
        type={type}
        value={form[key]}
        onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
        placeholder={placeholder}
        className="mt-1 bg-muted border-border text-foreground text-sm h-8 placeholder:text-muted-foreground"
        data-testid={`add-lead-${key}`}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="add-lead-modal">
        <div className="sticky top-0 bg-card border-b border-border px-5 py-4 flex items-center justify-between z-10">
          <h2 className="text-base font-semibold text-foreground">Add New Lead</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-muted text-muted-foreground"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {F('Full Name *', 'name', 'John Doe')}
            {F('Company', 'company', 'Acme Inc.')}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {F('Job Title', 'job_title', 'VP of Sales')}
            {F('Email', 'email', 'john@example.com', 'email')}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {F('Phone #', 'phone', '+1 555-000-0000', 'tel')}
            {F('Alt Phone(s)', 'alt_phones', '+1 555-111-2222, +1 555-333-4444')}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {F('Needs', 'needs', 'Looking for 3bd in Sacramento')}
            {F('Referral Source', 'referral_source', 'Website, Agent X, etc.')}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-foreground text-xs">Status</Label>
              <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                className="mt-1 w-full bg-muted border border-border rounded-md text-foreground text-sm h-8 px-2"
                data-testid="add-lead-status">
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="qualified">Qualified</option>
                <option value="active">Active</option>
                <option value="lost">Lost</option>
              </select>
            </div>
            <div>
              <Label className="text-foreground text-xs">Type</Label>
              <select value={form.lead_type} onChange={e => setForm(p => ({ ...p, lead_type: e.target.value }))}
                className="mt-1 w-full bg-muted border border-border rounded-md text-foreground text-sm h-8 px-2"
                data-testid="add-lead-type">
                <option value="unknown">Unknown</option>
                <option value="buyer">Buyer</option>
                <option value="seller">Seller</option>
                <option value="investor">Investor</option>
              </select>
            </div>
            {F('Assigned To', 'assigned_to', 'Agent name')}
          </div>
          {F('Last Contact', 'last_contact', '2026-01-15 or any date', 'text')}
          <div>
            <Label className="text-foreground text-xs">Notes</Label>
            <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="Any additional notes..."
              rows={2}
              className="mt-1 w-full bg-muted border border-border rounded-md text-foreground text-sm px-3 py-2 placeholder:text-muted-foreground resize-none"
              data-testid="add-lead-notes" />
          </div>
        </div>
        <div className="sticky bottom-0 bg-card border-t border-border px-5 py-3 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="text-muted-foreground">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="save-lead-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />}
            Add Lead
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ===== EXCEL UPLOAD ===== */
function ExcelUploadSection({ token, onImported }) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an .xlsx file');
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/leads/import`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      toast.success(`Imported ${data.imported} leads`);
      onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3" data-testid="excel-upload-section">
      <div className="border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-emerald-500/40 transition-colors">
        <Upload size={28} className="mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-foreground mb-1">Upload Excel Contact List</p>
        <p className="text-xs text-muted-foreground mb-3">Columns: Name, Company, Needs, Job Title, Email, Phone, Status, Notes, Assign, Last Contact, Referral Source</p>
        <label className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm rounded-lg cursor-pointer transition-colors">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? 'Importing...' : 'Choose .xlsx File'}
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleUpload} className="hidden" data-testid="excel-file-input" />
        </label>
      </div>
      {result && (
        <div className="bg-muted border border-border rounded-lg p-3 text-sm" data-testid="import-result">
          <div className="flex items-center gap-4 text-xs">
            <span className="text-emerald-400">{result.imported} imported</span>
            <span className="text-muted-foreground">{result.skipped} skipped</span>
            {result.errors?.length > 0 && <span className="text-red-400">{result.errors.length} errors</span>}
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-2 text-xs text-red-400 space-y-0.5">
              {result.errors.map((e, i) => <p key={e || i}>{e}</p>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ===== CLIENT LIST TABLE VIEW ===== */
function ClientListRow({ lead, onClick }) {
  return (
    <tr onClick={onClick} className="hover:bg-muted cursor-pointer transition-colors border-b border-border" data-testid={`client-row-${lead.id}`}>
      <td className="px-3 py-2.5 text-sm text-foreground font-medium truncate max-w-[140px]">{lead.name}</td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground truncate max-w-[120px]">{lead.company || '-'}</td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground truncate max-w-[120px]">{lead.needs || '-'}</td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground truncate max-w-[100px]">{lead.job_title || '-'}</td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground truncate max-w-[160px]">{lead.email || '-'}</td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
        {lead.phone || '-'}
        {lead.alt_phones?.length > 0 && (
          <span className="text-muted-foreground ml-1">(+{lead.alt_phones.length})</span>
        )}
      </td>
      <td className="px-3 py-2.5">
        <Badge className={`text-[10px] ${STATUS_STYLES[lead.status] || STATUS_STYLES.new}`}>
          {lead.status || 'new'}
        </Badge>
      </td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground truncate max-w-[100px]">{lead.notes || '-'}</td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground truncate max-w-[80px]">{lead.assigned_to || '-'}</td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{lead.last_contact ? formatDate(lead.last_contact) : formatDate(lead.updated_at)}</td>
      <td className="px-3 py-2.5 text-xs text-muted-foreground truncate max-w-[100px]">{lead.referral_source || lead.source || '-'}</td>
    </tr>
  );
}

/* ===== LEAD DETAIL (existing) ===== */
function LeadDetailPanel({ lead, onClose, token }) {
  const [activeTab, setActiveTab] = useState('info');
  if (!lead) return null;
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex justify-end" onClick={onClose} data-testid="lead-detail-overlay">
      <div className="w-full sm:max-w-lg bg-card border-l border-border h-full overflow-y-auto animate-in slide-in-from-right" onClick={e => e.stopPropagation()} data-testid="lead-detail-panel">
        <div className="sticky top-0 bg-card/95 backdrop-blur border-b border-border z-10">
          <div className="p-4 flex items-center justify-between">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground truncate">{lead.name}</h2>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge className={`text-xs ${TYPE_STYLES[lead.lead_type] || TYPE_STYLES.unknown}`}>{lead.lead_type}</Badge>
                <Badge className={`text-xs ${TEMP_STYLES[lead.temperature] || TEMP_STYLES.warm}`}>{lead.temperature}</Badge>
                {lead.company && <Badge className="text-xs bg-accent text-foreground">{lead.company}</Badge>}
                {lead.out_of_market && <Badge className="text-xs bg-orange-500/10 text-orange-400 border-orange-500/20">Out of Market</Badge>}
                {lead.referral_candidate && <Badge className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/20">Referral</Badge>}
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0" data-testid="close-lead-detail"><X size={18} /></button>
          </div>
          <div className="flex border-t border-border">
            {[
              { key: 'info', label: 'Info', icon: <User size={13} /> },
              { key: 'matches', label: 'Matches', icon: <Building2 size={13} />, count: (lead.property_matches || []).length },
              { key: 'calls', label: 'Calls', icon: <PhoneCall size={13} />, count: (lead.outbound_calls || []).length },
              { key: 'sms', label: 'SMS', icon: <MessageSquare size={13} />, count: (lead.sms_messages || []).length },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${activeTab === tab.key ? 'text-emerald-400 border-b-2 border-emerald-400' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid={`tab-${tab.key}`}>
                {tab.icon} {tab.label}
                {tab.count > 0 && <span className="bg-emerald-500/20 text-emerald-400 text-[10px] px-1.5 rounded-full">{tab.count}</span>}
              </button>
            ))}
          </div>
        </div>
        {activeTab === 'info' && <LeadInfoTab lead={lead} />}
        {activeTab === 'matches' && <LeadMatchesTab lead={lead} token={token} />}
        {activeTab === 'calls' && <LeadCallsTab lead={lead} token={token} />}
        {activeTab === 'sms' && <LeadSmsTab lead={lead} token={token} />}
      </div>
    </div>
  );
}

function LeadInfoTab({ lead }) {
  const bp = lead.buyer_profile;
  const sp = lead.seller_profile;
  const searches = lead.saved_searches || [];
  const followups = lead.followup_logs || [];
  const linkedReports = lead.linked_reports || [];

  return (
    <div className="p-4 space-y-5">
      {lead.recommended_next_action && (
        <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg p-3">
          <p className="text-xs text-emerald-400 font-medium mb-1">Recommended Next Action</p>
          <p className="text-sm text-foreground">{lead.recommended_next_action}</p>
        </div>
      )}
      {lead.last_call_outcome && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-xs text-muted-foreground">Last call outcome:</span>
          <Badge className="text-xs bg-muted text-foreground">{lead.last_call_outcome.replace(/_/g, ' ')}</Badge>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {lead.phone && <InfoBlock icon={<Phone size={14} />} label="Phone" value={lead.phone} />}
        {lead.email && <InfoBlock icon={<Mail size={14} />} label="Email" value={lead.email} />}
        {lead.company && <InfoBlock icon={<Building size={14} />} label="Company" value={lead.company} />}
        {lead.job_title && <InfoBlock icon={<Briefcase size={14} />} label="Job Title" value={lead.job_title} />}
        {lead.needs && <InfoBlock label="Needs" value={lead.needs} />}
        {lead.assigned_to && <InfoBlock icon={<User size={14} />} label="Assigned To" value={lead.assigned_to} />}
        {lead.referral_source && <InfoBlock label="Referral Source" value={lead.referral_source} />}
        {lead.last_contact && <InfoBlock icon={<Calendar size={14} />} label="Last Contact" value={lead.last_contact} />}
        {lead.preferred_contact_method && <InfoBlock label="Preferred Contact" value={lead.preferred_contact_method} />}
        {lead.language_preference && <InfoBlock label="Language" value={lead.language_preference} />}
      </div>
      {lead.alt_phones?.length > 0 && (
        <div className="bg-muted rounded-lg p-3">
          <p className="text-xs text-muted-foreground mb-1">Additional Phone Numbers</p>
          <div className="flex flex-wrap gap-2">
            {lead.alt_phones.map((p, i) => (
              <span key={i} className="text-sm text-foreground bg-accent/50 px-2 py-0.5 rounded">{p}</span>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {lead.out_of_market && <Badge className="text-xs bg-orange-500/10 text-orange-400 border-orange-500/20">Out of Market{lead.requested_area ? `: ${lead.requested_area}` : ''}</Badge>}
        {lead.referral_candidate && <Badge className="text-xs bg-amber-500/10 text-amber-400 border-amber-500/20">Referral Candidate</Badge>}
        {lead.callback_requested && <Badge className="text-xs bg-orange-500/10 text-orange-400 border-orange-500/20">Callback Requested</Badge>}
        {lead.appointment_interest && <Badge className="text-xs bg-emerald-500/10 text-emerald-400 border-emerald-500/20">Appointment Interest</Badge>}
        <Badge className="text-xs bg-muted text-muted-foreground">{lead.interaction_count || 1} interaction{(lead.interaction_count || 1) !== 1 ? 's' : ''}</Badge>
      </div>
      {lead.last_ai_summary && (
        <div>
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">AI Summary</h3>
          <p className="text-sm text-foreground bg-muted rounded-lg p-3">{lead.last_ai_summary}</p>
        </div>
      )}
      {bp && (
        <div>
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Buyer Preferences</h3>
          <div className="bg-muted rounded-lg p-3 space-y-2">
            {bp.preferred_cities?.length > 0 && <PrefRow icon={<MapPin size={12} />} label="Cities" value={bp.preferred_cities.join(', ')} />}
            {bp.preferred_zip_codes?.length > 0 && <PrefRow icon={<MapPin size={12} />} label="Zips" value={bp.preferred_zip_codes.join(', ')} />}
            {(bp.budget_min || bp.budget_max) && <PrefRow icon={<DollarSign size={12} />} label="Budget" value={`${bp.budget_min ? `$${bp.budget_min.toLocaleString()}` : ''} - ${bp.budget_max ? `$${bp.budget_max.toLocaleString()}` : ''}`} />}
            {bp.bedrooms_min && <PrefRow icon={<Home size={12} />} label="Beds" value={`${bp.bedrooms_min}+`} />}
            {bp.property_types?.length > 0 && <PrefRow icon={<Building size={12} />} label="Types" value={bp.property_types.join(', ')} />}
            {bp.timeline && <PrefRow icon={<Clock size={12} />} label="Timeline" value={bp.timeline} />}
          </div>
        </div>
      )}
      {sp && (
        <div>
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Seller Info</h3>
          <div className="bg-muted rounded-lg p-3 space-y-2">
            {sp.property_address && <PrefRow icon={<MapPin size={12} />} label="Address" value={sp.property_address} />}
            {sp.timeline && <PrefRow icon={<Clock size={12} />} label="Timeline" value={sp.timeline} />}
            {sp.reason_for_selling && <PrefRow label="Reason" value={sp.reason_for_selling} />}
          </div>
        </div>
      )}
      {followups.length > 0 && (
        <div>
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Follow-up Actions ({followups.length})</h3>
          <div className="space-y-2">
            {followups.slice(0, 5).map(fu => (
              <div key={fu.id} className="bg-muted rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <Badge className="text-[10px] bg-blue-500/10 text-blue-400 border-blue-500/20">{fu.action?.replace(/_/g, ' ')}</Badge>
                  <span className="text-[10px] text-muted-foreground">{formatDate(fu.created_at)}</span>
                </div>
                <p className="text-xs text-muted-foreground truncate">{fu.subject || fu.trigger?.replace(/_/g, ' ')}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {linkedReports.length > 0 && (
        <div>
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Linked Reports ({linkedReports.length})</h3>
          <div className="space-y-2">
            {linkedReports.slice(0, 3).map(r => (
              <div key={r.id} className="bg-muted rounded-lg p-3 flex items-center gap-2">
                <FileText size={12} className="text-blue-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground truncate">{r.subject}</p>
                  <p className="text-[10px] text-muted-foreground">{r.report_type?.replace(/_/g, ' ')} &middot; {formatDate(r.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {searches.length > 0 && (
        <div>
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Saved Searches ({searches.length})</h3>
          <div className="space-y-2">
            {searches.map(s => (
              <div key={s.id} className="bg-muted rounded-lg p-3 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm text-foreground truncate">{s.name}</p>
                  <p className="text-xs text-muted-foreground">{s.last_result_count} results &middot; {formatDate(s.last_run_at)}</p>
                </div>
                <Badge className="text-xs bg-indigo-500/10 text-indigo-400 border-indigo-500/20 shrink-0">{s.created_from}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
      {lead.notes_history?.length > 0 && (
        <div>
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Interaction History ({lead.notes_history.length})</h3>
          <div className="space-y-2">
            {lead.notes_history.map((note, i) => (
              <div key={i} className="bg-muted rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <Badge className="text-[10px] bg-accent text-foreground">{note.source}</Badge>
                  <span className="text-[10px] text-muted-foreground">{formatDate(note.timestamp)}</span>
                </div>
                <p className="text-xs text-muted-foreground">{note.summary}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const MATCH_TYPE_STYLES = {
  new_match: { label: 'New Match', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  price_drop: { label: 'Price Drop', color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
  status_change: { label: 'Status Change', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  back_on_market: { label: 'Back on Market', color: 'text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-500/20' },
  updated_match: { label: 'Updated', color: 'text-muted-foreground', bg: 'bg-secondary', border: 'border-border' },
};

function LeadMatchesTab({ lead, token }) {
  const matches = lead.property_matches || [];
  const [sending, setSending] = useState(null);
  const [analyses, setAnalyses] = useState({});
  useEffect(() => {
    const fetchAnalyses = async () => {
      const headers = { Authorization: `Bearer ${token}` };
      const propIds = [...new Set(matches.map(m => m.property_id))];
      for (const pid of propIds.slice(0, 10)) {
        try {
          const { data } = await axios.get(`${API}/property-analysis/${pid}`, { headers });
          setAnalyses(prev => ({ ...prev, [pid]: data }));
        } catch { /* no analysis yet */ }
      }
    };
    if (matches.length > 0) fetchAnalyses();
  }, [matches.length, token]);
  const handleSendNotification = async (matchId, channels) => {
    setSending(matchId);
    try {
      await axios.post(`${API}/property-matches/${matchId}/send`, { channels }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Notification sent');
    } catch { toast.error('Failed to send notification'); }
    finally { setSending(null); }
  };
  if (matches.length === 0) return (
    <div className="p-6 text-center"><Building2 className="h-8 w-8 text-muted-foreground mx-auto mb-2" /><p className="text-sm text-muted-foreground">No property matches yet</p></div>
  );
  return (
    <div className="p-3 space-y-2 max-h-[500px] overflow-y-auto" data-testid="lead-matches-tab">
      {matches.map(m => {
        const style = MATCH_TYPE_STYLES[m.match_type] || MATCH_TYPE_STYLES.new_match;
        const snap = m.property_snapshot || {};
        return (
          <div key={m.id} className="bg-muted rounded-lg p-3 space-y-2 border border-border">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <Badge className={`text-[10px] ${style.bg} ${style.color} ${style.border}`}>{style.label}</Badge>
                <p className="text-sm text-foreground font-medium truncate mt-1">{snap.address_line || 'Property'}</p>
                <p className="text-xs text-muted-foreground">{snap.city}, {snap.state} {snap.postal_code}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="text-teal-400 font-semibold">${snap.list_price?.toLocaleString()}</span>
              <span>{snap.beds}bd / {snap.baths}ba</span>
              <span>{snap.sqft?.toLocaleString()} sqft</span>
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-border">
              <span className="text-[10px] text-muted-foreground">{formatDate(m.created_at)}</span>
              <div className="flex gap-1">
                {!m.alert_sent_sms && lead.phone && (
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] text-teal-400 hover:bg-teal-500/10 px-2" disabled={sending === m.id} onClick={() => handleSendNotification(m.id, ['sms'])}><Send size={10} className="mr-1" /> SMS</Button>
                )}
                {!m.alert_sent_email && lead.email && (
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] text-blue-400 hover:bg-blue-500/10 px-2" disabled={sending === m.id} onClick={() => handleSendNotification(m.id, ['email'])}><Mail size={10} className="mr-1" /> Email</Button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const OUTCOME_STYLES = {
  answered: { icon: <CheckCircle2 size={12} />, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  transferred: { icon: <ArrowUpRight size={12} />, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  voicemail: { icon: <Voicemail size={12} />, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  no_answer: { icon: <PhoneMissed size={12} />, color: 'text-muted-foreground', bg: 'bg-secondary' },
  failed: { icon: <AlertTriangle size={12} />, color: 'text-red-400', bg: 'bg-red-500/10' },
};

function LeadCallsTab({ lead, token }) {
  const [calls, setCalls] = useState(lead.outbound_calls || []);
  const [calling, setCalling] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [schedDate, setSchedDate] = useState('');
  const [schedTime, setSchedTime] = useState('10:00');
  const [schedPurpose, setSchedPurpose] = useState('follow_up');
  const [schedNotes, setSchedNotes] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const { recs } = useScheduleRecommendations(token);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchScheduled = async () => {
      try {
        const { data } = await axios.get(`${API}/scheduled-calls?status=pending`, { headers });
        setScheduledCalls(data.filter(sc => sc.lead_id === lead.id));
      } catch { /* silent */ }
    };
    fetchScheduled();
  }, [lead.id]);

  const startCall = async () => {
    if (!lead.phone) return;
    setCalling(true);
    try {
      const { data } = await axios.post(`${API}/calls/outbound/start`, { lead_id: lead.id }, { headers });
      setCalls(prev => [data, ...prev]);
      toast.success(`Call ${data.status === 'completed' ? 'completed' : data.status}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to initiate call'); }
    finally { setCalling(false); }
  };

  const scheduleCall = async () => {
    if (!schedDate || !schedTime) return;
    setScheduling(true);
    try {
      const scheduled_at = new Date(`${schedDate}T${schedTime}:00`).toISOString();
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { data } = await axios.post(`${API}/scheduled-calls`, { lead_id: lead.id, scheduled_at, purpose: schedPurpose, notes: schedNotes || null, timezone: tz }, { headers });
      setScheduledCalls(prev => [...prev, data]);
      setShowSchedule(false);
      toast.success('Call scheduled');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to schedule'); }
    finally { setScheduling(false); }
  };

  const o = (outcome) => OUTCOME_STYLES[outcome] || OUTCOME_STYLES.failed;

  return (
    <div className="flex flex-col h-[calc(100vh-140px)]" data-testid="calls-tab">
      <div className="p-3 border-b border-border space-y-2">
        <div className="flex gap-2">
          <Button size="sm" onClick={startCall} disabled={calling || !lead.phone} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-foreground h-9 gap-2" data-testid="call-now-btn">
            {calling ? <Loader2 size={14} className="animate-spin" /> : <PhoneCall size={14} />} Call Now
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowSchedule(!showSchedule)} disabled={!lead.phone} className="border-border text-foreground hover:bg-muted h-9 gap-1.5" data-testid="schedule-call-btn">
            <Clock size={14} /> Schedule
          </Button>
        </div>
        {showSchedule && (
          <div className="bg-muted border border-border rounded-lg p-3 space-y-2 animate-in fade-in" data-testid="schedule-form">
            <div className="flex gap-2">
              <input type="date" value={schedDate} onChange={e => setSchedDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="flex-1 bg-card border border-border rounded px-2 py-1.5 text-xs text-foreground" />
              <input type="time" value={schedTime} onChange={e => setSchedTime(e.target.value)} className="w-24 bg-card border border-border rounded px-2 py-1.5 text-xs text-foreground" />
            </div>
            <select value={schedPurpose} onChange={e => setSchedPurpose(e.target.value)} className="w-full bg-card border border-border rounded px-2 py-1.5 text-xs text-foreground">
              <option value="follow_up">Follow-Up</option>
              <option value="showing_reminder">Showing Reminder</option>
              <option value="new_lead_outreach">New Lead Outreach</option>
            </select>
            <InlineTimeSuggestion recs={recs} purpose={schedPurpose} />
            <Button size="sm" onClick={scheduleCall} disabled={scheduling || !schedDate} className="w-full bg-teal-600 hover:bg-teal-700 text-foreground h-8 text-xs">Confirm</Button>
          </div>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {calls.length === 0 && <div className="flex items-center justify-center h-32"><p className="text-sm text-muted-foreground">No outbound calls yet</p></div>}
        {calls.map(call => {
          const style = o(call.outcome);
          return (
            <div key={call.id} className="bg-muted border border-border/40 rounded-lg p-3 cursor-pointer hover:bg-muted transition-colors" onClick={() => setExpanded(expanded === call.id ? null : call.id)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`p-1 rounded ${style.bg}`}><span className={style.color}>{style.icon}</span></div>
                  <div>
                    <p className="text-xs font-medium text-foreground">{call.outcome || call.status}</p>
                    <p className="text-[10px] text-muted-foreground">{call.created_at ? new Date(call.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</p>
                  </div>
                </div>
              </div>
              {expanded === call.id && call.summary && <p className="mt-2 pt-2 border-t border-border text-xs text-foreground">{call.summary}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LeadSmsTab({ lead, token }) {
  const [messages, setMessages] = useState(lead.sms_messages || []);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [optedOut] = useState(lead.sms_opt_out || false);
  const scrollRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);
  const sendMessage = async () => {
    if (!newMsg.trim() || !lead.phone) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/sms/send`, { lead_id: lead.id, body: newMsg.trim() }, { headers });
      setMessages(prev => [...prev, data]);
      setNewMsg('');
      toast.success('SMS sent');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
    finally { setSending(false); }
  };
  return (
    <div className="flex flex-col h-[calc(100vh-140px)]" data-testid="sms-tab">
      {optedOut && <div className="mx-4 mt-3 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center gap-2"><Ban size={14} className="text-amber-400" /><span className="text-xs text-amber-300">Lead opted out of SMS</span></div>}
      {lead.phone && (
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && <div className="flex items-center justify-center h-full"><p className="text-sm text-muted-foreground">No messages yet</p></div>}
            {messages.map(msg => (
              <div key={msg.id} className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-xl px-3 py-2 ${msg.direction === 'outbound' ? 'bg-emerald-600/20 border border-emerald-500/20' : 'bg-muted border border-border'}`}>
                  <p className="text-sm text-foreground break-words">{msg.body}</p>
                  <span className="text-[10px] text-muted-foreground block text-right mt-1">{msg.sent_at ? new Date(msg.sent_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-border p-3 bg-card/95">
            <div className="flex items-center gap-2">
              <Input value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }} placeholder={optedOut ? 'Opted out' : 'Type a message...'} disabled={optedOut} className="flex-1 bg-muted border-border text-foreground text-sm h-9" data-testid="sms-input" />
              <Button size="sm" onClick={sendMessage} disabled={sending || !newMsg.trim() || optedOut} className="bg-emerald-600 hover:bg-emerald-700 text-foreground h-9 px-3" data-testid="sms-send-btn">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function InfoBlock({ icon, label, value }) {
  return (
    <div className="bg-muted rounded-lg p-3">
      <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">{icon}{label}</p>
      <p className="text-sm text-foreground truncate">{value}</p>
    </div>
  );
}

function PrefRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {icon && <span className="text-muted-foreground">{icon}</span>}
      <span className="text-muted-foreground">{label}:</span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

function LeadRow({ lead, onClick }) {
  const typeStyle = TYPE_STYLES[lead.lead_type] || TYPE_STYLES.unknown;
  const priority = PRIORITY_STYLES[lead.priority_label] || PRIORITY_STYLES.cold;
  const bp = lead.buyer_profile;
  const prefSummary = bp
    ? [bp.preferred_cities?.join(', '), bp.budget_max ? `under $${(bp.budget_max / 1000).toFixed(0)}k` : null, bp.bedrooms_min ? `${bp.bedrooms_min}+ beds` : null].filter(Boolean).join(' | ')
    : lead.notes?.slice(0, 60);
  return (
    <div onClick={onClick} className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 hover:bg-muted cursor-pointer transition-colors border-b border-border last:border-0" data-testid={`lead-row-${lead.id}`}>
      <div className={`h-8 w-8 sm:h-9 sm:w-9 rounded-lg ${priority.bg} flex items-center justify-center shrink-0 border ${priority.border}`}>
        <span className={`text-xs font-bold ${priority.text}`}>{lead.priority_score || 0}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-medium text-foreground truncate">{lead.name}</span>
          <Badge className={`text-[10px] ${typeStyle}`}>{lead.lead_type}</Badge>
          <Badge className={`text-[10px] ${priority.bg} ${priority.text} ${priority.border} hidden sm:inline-flex`}>{priority.label}</Badge>
          {lead.company && <Badge className="text-[10px] bg-accent text-foreground hidden sm:inline-flex">{lead.company}</Badge>}
        </div>
        <p className="text-xs text-muted-foreground truncate mt-0.5">{prefSummary || 'No preferences yet'}</p>
      </div>
      <div className="text-right shrink-0 hidden sm:block">
        <p className="text-xs text-muted-foreground">{formatDate(lead.updated_at || lead.created_at)}</p>
      </div>
      <ArrowRight size={14} className="text-muted-foreground shrink-0" />
    </div>
  );
}

/* ===== MAIN PAGE ===== */
export default function LeadsPage() {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('priority');
  const [viewMode, setViewMode] = useState('cards'); // cards | table
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchData = useCallback(async () => {
    const hdrs = { Authorization: `Bearer ${token}` };
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads/?limit=200`, { headers: hdrs }),
        axios.get(`${API}/leads/stats`, { headers: hdrs }),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openLeadDetail = async (lead) => {
    try {
      const { data } = await axios.get(`${API}/leads/${lead.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedLead(data);
    } catch { setSelectedLead(lead); }
  };

  const filtered = leads.filter(l => {
    if (filterType && l.lead_type !== filterType) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (l.name || '').toLowerCase().includes(q) || (l.phone || '').includes(q) || (l.email || '').toLowerCase().includes(q) || (l.company || '').toLowerCase().includes(q);
    }
    return true;
  }).sort((a, b) => {
    if (sortBy === 'priority') return (b.priority_score || 0) - (a.priority_score || 0);
    return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
  });

  if (loading) return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent" /></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-5 sm:space-y-6" data-testid="leads-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">Leads</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">CRM intelligence from AI conversations</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setShowAddModal(true)} className="bg-emerald-500 hover:bg-emerald-600 text-white h-8 gap-1.5" data-testid="add-lead-btn">
            <Plus size={14} /> Add Lead
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total Leads" value={stats?.total || 0} />
        <StatCard label="Buyers" value={stats?.buyers || 0} accent="blue" />
        <StatCard label="Sellers" value={stats?.sellers || 0} accent="amber" />
        <StatCard label="Callbacks" value={stats?.callback_needed || 0} accent="orange" />
      </div>

      {/* View toggle + Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative w-full sm:max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search leads..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 bg-muted border-border text-foreground text-sm h-9" data-testid="lead-search-input" />
        </div>
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 w-full sm:w-auto">
          {['', 'buyer', 'seller', 'investor', 'unknown'].map(t => (
            <button key={t} onClick={() => setFilterType(filterType === t ? '' : t)}
              className={`px-2.5 py-1 rounded-full text-xs transition-colors whitespace-nowrap ${filterType === t ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-muted text-muted-foreground border border-border hover:bg-muted'}`}
              data-testid={`filter-${t || 'all'}`}>{t || 'All'}</button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 sm:ml-auto">
          <span className="text-xs text-muted-foreground">View:</span>
          <button onClick={() => setViewMode('cards')} className={`px-2 py-1 rounded text-xs ${viewMode === 'cards' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-muted text-muted-foreground'}`} data-testid="view-cards">Cards</button>
          <button onClick={() => setViewMode('table')} className={`px-2 py-1 rounded text-xs ${viewMode === 'table' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-muted text-muted-foreground'}`} data-testid="view-table">Client List</button>
        </div>
      </div>

      {/* Excel Upload (visible in table view) */}
      {viewMode === 'table' && (
        <ExcelUploadSection token={token} onImported={fetchData} />
      )}

      {/* Lead list - Cards view */}
      {viewMode === 'cards' && (
        <Card className="border-border bg-card overflow-hidden" data-testid="lead-list-card">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-foreground text-sm font-medium">{filtered.length} lead{filtered.length !== 1 ? 's' : ''}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filtered.length === 0 ? (
              <div className="py-12 text-center">
                <User className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No leads yet</p>
                <p className="text-xs text-muted-foreground mt-1">Add leads manually or upload an Excel file</p>
              </div>
            ) : filtered.map(lead => <LeadRow key={lead.id} lead={lead} onClick={() => openLeadDetail(lead)} />)}
          </CardContent>
        </Card>
      )}

      {/* Lead list - Table/Client List view */}
      {viewMode === 'table' && (
        <Card className="border-border bg-card overflow-hidden" data-testid="client-list-card">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-border bg-muted">
                  {['Name', 'Company', 'Needs', 'Job Title', 'Email', 'Phone #', 'Status', 'Notes', 'Assign', 'Last Contact', 'Referral'].map(h => (
                    <th key={h} className="px-3 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={11} className="py-12 text-center text-sm text-muted-foreground">No leads yet</td></tr>
                ) : filtered.map(lead => <ClientListRow key={lead.id} lead={lead} onClick={() => openLeadDetail(lead)} />)}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {selectedLead && <LeadDetailPanel lead={selectedLead} onClose={() => setSelectedLead(null)} token={token} />}
      <AddLeadModal open={showAddModal} onClose={() => setShowAddModal(false)} token={token} onCreated={fetchData} />
    </div>
  );
}

function StatCard({ label, value, accent = 'slate' }) {
  const colors = { slate: 'text-foreground', blue: 'text-blue-400', amber: 'text-amber-400', orange: 'text-orange-400' };
  return (
    <Card className="border-border bg-card">
      <CardContent className="p-4">
        <p className={`text-xl font-bold ${colors[accent]}`}>{value}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
      </CardContent>
    </Card>
  );
}
